// src/pages/FormsObjetivo.jsx
//
// O orquestrador da conversa de onboarding. Ele é dono de `currentStep`,
// `answers`, `next`, `back`, do auto-advance e do `saveUserObjective`. O
// `ChatObjetivo` só desenha a conversa em volta; cada step desenha UM widget.
//
// O QUE MUDOU NESTA EVOLUÇÃO, além da casca:
//  - quatro perguntas novas (dias, tempo por treino, local, refeições), que o
//    Objetivo.dc.html previa e o formulário antigo não tinha;
//  - a ordem passou a ser a do desenho — altura, peso, meta, ritmo — porque o
//    card de projeção só existe DEPOIS de peso e meta;
//  - o fim do fluxo dispara a geração do plano (`onGerarPlano`). Antes o
//    onboarding terminava e a pessoa tinha de encontrar o gerador na aba
//    Treino sozinha.
//
// O que NÃO mudou: o payload é o mesmo blob de sempre, gravado pelo mesmo
// `saveUserObjective`, com as mesmas chaves para todos os campos que já
// existiam.

import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";

import Seo from "../components/layout/Seo.jsx";
import { seo } from "../data/seoData";
import callFirebaseFunction from "../utils/firebaseFunctionCaller.js";
import haptics from "../design/feedback/haptics";

// steps
import StepName from "../features/objetivo/steps/StepName.jsx";
import StepWelcome from "../features/objetivo/steps/StepWelcome.jsx";
import StepQuestionObjective from "../features/objetivo/steps/StepQuestionObjective.jsx";
import StepGender from "../features/objetivo/steps/StepGender.jsx";
import StepCurrentHeight from "../features/objetivo/steps/StepCurrentHeight.jsx";
import StepCurrentWeight from "../features/objetivo/steps/StepCurrentWeight.jsx";
import StepTargetWeight from "../features/objetivo/steps/StepTargetWeight.jsx";
import StepTimeframe from "../features/objetivo/steps/StepTimeframe.jsx";
import StepActivityLevel from "../features/objetivo/steps/StepActivityLevel.jsx";
import StepInterest from "../features/objetivo/steps/StepInterest.jsx";
import StepDiasTreino from "../features/objetivo/steps/StepDiasTreino.jsx";
import StepTempoTreino from "../features/objetivo/steps/StepTempoTreino.jsx";
import StepLocalTreino from "../features/objetivo/steps/StepLocalTreino.jsx";
import StepRefeicoes from "../features/objetivo/steps/StepRefeicoes.jsx";
import StepResAlimentares from "../features/objetivo/steps/StepResAlimentares.jsx";
import StepPrefAlimentares from "../features/objetivo/steps/StepPrefAlimentares.jsx";
import StepSpecialCare from "../features/objetivo/steps/StepSpecialCare.jsx";
import StepWeightLossMedication from "../features/objetivo/steps/StepWeightLossMedication.jsx";
import StepSchedule from "../features/objetivo/steps/StepSchedule.jsx";
import StepConclusion from "../features/objetivo/steps/StepConclusion.jsx";

import ChatObjetivo from "../features/objetivo/ChatObjetivo.jsx";
import MontandoPlano from "../features/objetivo/MontandoPlano.jsx";
import { registrarConversa } from "../features/objetivo/conversa.js";
import { respostasDaGeracao } from "../features/objetivo/mapaGeracao.js";
import { usePlanoGeracao } from "../features/plano/PlanoGeracaoContext.jsx";
import Style from "../features/objetivo/FormsObjetivo.module.scss";

// A ORDEM É A DO Objetivo.dc.html: boas → objetivo → sexo → altura → peso →
// meta → ritmo → nível → músculos → dias → tempo → local → refeições →
// restrições → estilo → saúde → horários → fim.
const baseSteps = [
  StepWelcome,
  StepQuestionObjective,
  StepGender,
  StepCurrentHeight,
  StepCurrentWeight,
  StepTargetWeight,
  StepTimeframe,
  StepActivityLevel,
  StepInterest,
  StepDiasTreino,
  StepTempoTreino,
  StepLocalTreino,
  StepRefeicoes,
  StepResAlimentares,
  StepPrefAlimentares,
  StepSpecialCare,
  StepWeightLossMedication,
  StepSchedule,
  StepConclusion,
];

// Liga cada step à sua copy de chat. É aqui e não dentro de `conversa.js`
// porque é este arquivo que conhece os componentes — e é uma propriedade
// explícita (`chatId`), não `Function.name`, que o terser do build de
// produção não preserva.
registrarConversa({
  StepName,
  StepWelcome,
  StepQuestionObjective,
  StepGender,
  StepCurrentHeight,
  StepCurrentWeight,
  StepTargetWeight,
  StepTimeframe,
  StepActivityLevel,
  StepInterest,
  StepDiasTreino,
  StepTempoTreino,
  StepLocalTreino,
  StepRefeicoes,
  StepResAlimentares,
  StepPrefAlimentares,
  StepSpecialCare,
  StepWeightLossMedication,
  StepSchedule,
  StepConclusion,
});

// Steps que avançam sozinhos após uma seleção ÚNICA. Os de seleção múltipla
// (chips, dias, mapa) e os que abrem campo de texto pedem o botão por conta
// própria, via `setNeedsButton(true)` — a mesma alavanca de sempre.
const AUTO_ADVANCE_STEPS = [
  StepWelcome,
  StepQuestionObjective,
  StepGender,
  StepTimeframe,
  StepActivityLevel,
  StepTempoTreino,
  StepLocalTreino,
  StepSpecialCare,
  StepWeightLossMedication,
];

const FormsObjetivo = ({
  patientUserID,
  startStep = 0,
  userProfile,
  /** Fecha o onboarding — usado pelo offcanvas "Refazer objetivo". */
  onConcluir,
  onSolicitarSaida,
  onProgressoChange,
  permitirSaida = false,
  /**
   * Lista de steps a percorrer. Sem ela, a conversa inteira.
   *
   * Existe para o CAMINHO EXPRESSO (`ObjetivoExpresso`): quem já está dentro
   * do app e só precisa dos campos que faltam não deveria responder as
   * dezoito perguntas de novo.
   */
  steps,
  /**
   * Respostas que a pessoa JÁ TEM, e que entram no save junto com as novas.
   *
   * SEM ISSO O ATALHO SERIA UMA ARMADILHA. `saveUserObjective` cria um
   * documento NOVO e arquiva o anterior — ele não faz merge. Salvar só os
   * campos do caminho expresso deixaria o objetivo ativo sem TRAINING_DAYS,
   * MEALS_PER_DAY, TRAINING_PLACE e as restrições alimentares, e o próximo
   * plano sairia pior do que o atual, sem ninguém entender por quê.
   */
  respostasBase,
  /** Leva para a avaliação corporal a partir do banner do fim. */
  onAvaliacaoCorporal,
}) => {
  /* Quem gera o plano é o provider do shell: é ele que acende os dois botões
     flutuantes e acompanha cada artefato. Fora dele (testes, tela solta) o
     hook devolve um no-op — o onboarding salva e termina do mesmo jeito. */
  const { iniciar: iniciarGeracaoDoPlano } = usePlanoGeracao();

  const finalSteps = useMemo(() => {
    if (Array.isArray(steps) && steps.length) return steps;
    if (!userProfile?.NOME) return [StepName, ...baseSteps];
    return baseSteps;
  }, [steps, userProfile]);

  const [currentStep, setCurrentStep] = useState(startStep);
  /* As respostas anteriores entram JÁ NO ESTADO INICIAL, não só no save.
     Assim os steps que leem o que veio antes (StepTargetWeight lê
     CURRENT_WEIGHT) funcionam igual dentro do atalho, e o merge acontece
     sozinho — `novasRespostas` já carrega a base. */
  const [answers, setAnswers] = useState(() => ({ ...(respostasBase || {}) }));
  const [canProceed, setCanProceed] = useState(false);
  const [stepData, setStepData] = useState(null);
  const [stepWantsButton, setStepWantsButton] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState("");
  const [houveResposta, setHouveResposta] = useState(false);

  // Alguns steps têm fases internas (o de rotina tem sono → treino). Quando
  // há uma fase a desfazer, a seta do cabeçalho desfaz A FASE, não o step:
  // um toque, uma coisa desfeita.
  const voltarInternoRef = useRef(null);
  const registrarVoltarInterno = useCallback((fn) => {
    voltarInternoRef.current = fn || null;
  }, []);

  const totalSteps = finalSteps.length;
  const StepComponent = finalSteps[currentStep];
  const ehUltimo = currentStep === totalSteps - 1;

  useEffect(() => {
    setStepWantsButton(false);
    setCanProceed(false);
    setStepData(null);
    setErro("");
  }, [currentStep, finalSteps]);

  /** Salva o objetivo, registra o aceite e dispara a geração do plano. */
  const concluir = useCallback(
    async (respostasFinais) => {
      setEnviando(true);
      setErro("");
      try {
        const functions = getFunctions(undefined, "southamerica-east1");
        const saveUserObjective = httpsCallable(functions, "saveUserObjective");
        await saveUserObjective(respostasFinais);

        /* Registro da solicitação do plano por IA — trilha de auditoria, não
           pergunta. A seção 8 dos Termos de Uso ("Consentimento Informado —
           VitAI"), aceita no cadastro, fala em registro com data, hora e
           versão a cada solicitação; é o que esta chamada grava.
           NÃO bloqueia: o aceite que autoriza a geração já foi dado, e
           falhar em anotar não pode impedir a pessoa de receber o plano. */
        callFirebaseFunction("saveVitAIConsent", { versao: "1.0", aceite: true })
          .catch((err) => console.error("Falha ao registrar a solicitação VitAI:", err));

        // Sem `await`: a geração leva minutos e acontece em segundo plano.
        iniciarGeracaoDoPlano(respostasDaGeracao(respostasFinais));

        haptics.success();
        onConcluir?.(respostasFinais);
      } catch (err) {
        console.error("Erro ao salvar objetivo:", err);
        setErro("Não deu pra salvar agora. Confere a conexão e tenta de novo.");
        setEnviando(false);
      }
    },
    [iniciarGeracaoDoPlano, onConcluir]
  );

  const next = useCallback(async () => {
    if (!canProceed || enviando) return;

    const novasRespostas = stepData ? { ...answers, ...stepData } : answers;
    if (stepData) {
      setAnswers(novasRespostas);
      if (!houveResposta) {
        setHouveResposta(true);
        onProgressoChange?.(true);
      }
    }

    haptics.light();

    if (ehUltimo) {
      await concluir(novasRespostas);
      return;
    }

    setCanProceed(false);
    setStepData(null);
    setCurrentStep((prev) => prev + 1);
  }, [canProceed, enviando, stepData, answers, ehUltimo, concluir, houveResposta, onProgressoChange]);

  const back = useCallback(() => {
    // Fase interna primeiro: só quando não há nenhuma é que o step sai.
    if (voltarInternoRef.current) {
      voltarInternoRef.current();
      return;
    }
    if (houveResposta && currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
      return;
    }
    if (!houveResposta) {
      if (onSolicitarSaida) onSolicitarSaida();
      else onConcluir?.(null);
    }
  }, [currentStep, houveResposta, onConcluir, onSolicitarSaida]);

  // Sempre aponta para o next mais recente, evitando closure stale no timer.
  const nextRef = useRef(next);
  useEffect(() => { nextRef.current = next; });

  // Auto-avança 350 ms após a seleção nos steps configurados.
  useEffect(() => {
    if (!canProceed || stepWantsButton) return undefined;
    if (!AUTO_ADVANCE_STEPS.includes(StepComponent)) return undefined;
    const t = setTimeout(() => nextRef.current(), 350);
    return () => clearTimeout(t);
  }, [canProceed, stepWantsButton, StepComponent]);

  /* A barra de progresso não conta as boas-vindas nem o fim: contar o "oi"
     como progresso dá 6% antes de a pessoa responder qualquer coisa. */
  const hasNameStep = finalSteps.includes(StepName);
  const inicio = hasNameStep ? 2 : 1;
  const mostrarProgresso = currentStep >= inicio && currentStep < totalSteps - 1;

  const primeiroNome =
    String(userProfile?.NOME || answers?.NOME || "").trim().split(/\s+/)[0] || "";

  // O rodapé aparece quando o step não avança sozinho — ou quando ele pediu
  // o botão (campo de texto aberto, seleção múltipla).
  const mostrarRodape = !AUTO_ADVANCE_STEPS.includes(StepComponent) || stepWantsButton;

  const rotuloDoBotao = () => {
    if (enviando) return "Salvando…";
    if (ehUltimo) return "Gerar meu plano";
    if (canProceed) return "Continuar";
    return "Escolhe pelo menos uma";
  };

  const rodape = mostrarRodape ? (
    <>
      {erro && <p className={Style.erro} role="alert">{erro}</p>}
      <button
        type="button"
        className={Style.cta}
        onClick={next}
        disabled={!canProceed || enviando}
      >
        {rotuloDoBotao()}
      </button>
    </>
  ) : null;

  return (
    <>
      <Seo {...seo.objetivos} />

      <ChatObjetivo
        steps={finalSteps}
        indiceAtual={currentStep}
        answers={answers}
        nome={primeiroNome}
        onVoltar={back}
        onInicio={onSolicitarSaida}
        mostrarInicio={permitirSaida && houveResposta}
        mostrarProgresso={mostrarProgresso}
        rodape={rodape}
      >
        <StepComponent
          answers={answers}
          patientUserID={patientUserID}
          setCanProceed={setCanProceed}
          setStepData={setStepData}
          setNeedsButton={setStepWantsButton}
          registrarVoltarInterno={registrarVoltarInterno}
          onAvaliacaoCorporal={onAvaliacaoCorporal}
        />
      </ChatObjetivo>

      {/* A tela de "montando" cobre o salvamento e o disparo da geração. O
          plano de verdade continua sendo montado depois, em segundo plano —
          quem mostra isso são os dois botões flutuantes da esquerda. */}
      {enviando && <MontandoPlano />}
    </>
  );
};

export default FormsObjetivo;
